import {
  ChartNoAxesColumn,
  Contact,
  Funnel,
  LayoutGrid,
  ListTodo,
  LogOut,
  SlidersHorizontal,
  User,
  Users,
  type LucideIcon,
} from "lucide-react";
import { CanAccess, useGetIdentity, useLogout, useTranslate } from "ra-core";
import { Link, matchPath, useLocation } from "react-router";
import { LocalesMenuButton } from "@/components/admin/locales-menu-button";
import { ThemeModeToggle } from "@/components/admin/theme-mode-toggle";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarSeparator,
  SidebarTrigger,
  useSidebar,
} from "@/components/ui/sidebar";

import { useConfigurationContext } from "../root/ConfigurationContext";
import { GlobalSearchButton } from "../search/GlobalSearchButton";

/** "Oselya" wordmark with the honey dot (docs/design/Brand.dc.html) */
export const Wordmark = ({ title }: { title: string }) => (
  <span className="inline-flex items-center text-xl font-extrabold tracking-tight text-primary leading-none">
    {title}
    <i
      aria-hidden
      className="ml-[0.06em] inline-block size-[0.3em] rounded-full bg-honey"
    />
  </span>
);

const NavItem = ({
  to,
  label,
  icon: Icon,
  match,
}: {
  to: string;
  label: string;
  icon: LucideIcon;
  match?: string;
}) => {
  const location = useLocation();
  const { isMobile, setOpenMobile } = useSidebar();
  const isActive =
    to === "/"
      ? location.pathname === "/"
      : matchPath(`${match ?? to}/*`, location.pathname) != null;
  return (
    <SidebarMenuItem>
      <SidebarMenuButton
        render={<Link to={to} />}
        isActive={isActive}
        tooltip={label}
        onClick={() => isMobile && setOpenMobile(false)}
      >
        <Icon />
        <span>{label}</span>
      </SidebarMenuButton>
    </SidebarMenuItem>
  );
};

export const AppSidebar = () => {
  const translate = useTranslate();
  const { title } = useConfigurationContext();
  const { identity } = useGetIdentity();
  const logout = useLogout();
  const { state } = useSidebar();

  return (
    <Sidebar collapsible="icon">
      <SidebarHeader>
        <div className="flex items-center justify-between gap-2 px-2 pt-1 group-data-[collapsible=icon]:px-0">
          <Link
            to="/"
            className="no-underline group-data-[collapsible=icon]:hidden"
          >
            <Wordmark title={title} />
          </Link>
          <SidebarTrigger />
        </div>
        {state === "expanded" ? (
          <GlobalSearchButton className="mr-0 w-full lg:w-full" />
        ) : null}
      </SidebarHeader>

      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              <CanAccess resource="dashboard" action="list">
                <NavItem
                  to="/"
                  label={translate("ra.page.dashboard")}
                  icon={ChartNoAxesColumn}
                />
              </CanAccess>
              <CanAccess resource="deals" action="list">
                <NavItem
                  to="/funnel"
                  label={translate("crm.navigation.pipeline")}
                  icon={Funnel}
                />
              </CanAccess>
              <CanAccess resource="apartments" action="list">
                <NavItem
                  to="/apartments"
                  label={translate("crm.navigation.apartments")}
                  icon={LayoutGrid}
                />
              </CanAccess>
              <CanAccess resource="tasks" action="list">
                <NavItem
                  to="/tasks"
                  label={translate("crm.navigation.tasks")}
                  icon={ListTodo}
                />
              </CanAccess>
              <CanAccess resource="contacts" action="list">
                <NavItem
                  to="/contacts"
                  label={translate("resources.contacts.name", {
                    smart_count: 2,
                  })}
                  icon={Contact}
                />
              </CanAccess>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter>
        <SidebarSeparator className="mx-0" />
        <SidebarMenu>
          <CanAccess resource="sales" action="list">
            <NavItem
              to="/sales"
              label={translate("crm.navigation.team")}
              icon={Users}
            />
          </CanAccess>
          <CanAccess resource="configuration" action="edit">
            <NavItem
              to="/settings"
              label={translate("crm.settings.title")}
              icon={SlidersHorizontal}
            />
          </CanAccess>
          <NavItem
            to="/profile"
            label={translate("crm.profile.title")}
            icon={User}
          />
          <SidebarMenuItem>
            <SidebarMenuButton
              onClick={() => logout()}
              tooltip={translate("ra.auth.logout")}
              className="text-destructive hover:text-destructive"
            >
              <LogOut />
              <span>{translate("ra.auth.logout")}</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
        <div className="flex items-center justify-between gap-1 px-2 group-data-[collapsible=icon]:hidden">
          <span className="truncate text-xs text-muted-foreground">
            {identity?.roleName}
          </span>
          <span className="flex items-center">
            <LocalesMenuButton />
            <ThemeModeToggle />
          </span>
        </div>
      </SidebarFooter>
    </Sidebar>
  );
};
